import React from "react";
import PropTypes from "prop-types";

const StepIndicator = ({
  currentStep,
  totalSteps,
  loading,
  setCurrentStep,
}) => {
  const getStepLabel = (stepIndex) => {
    switch (stepIndex) {
      case 0:
        return "Infos principales";
      case 1:
        return "Auteurs";
      case 2:
        return "Fichiers & Matériel";
      case 3:
        return "Contenu";
      case 4: 
        return "Referencement";
      case 5:
        return "Finalisation";
      default:
        return `Étape ${stepIndex + 1}`;
    }
  };

  const getStepClasses = (stepIndex) => {
    const baseClasses =
      "flex h-12 w-12 items-center justify-center rounded-full font-semibold text-sm transition-all duration-200";

    if (currentStep === stepIndex + 1) {
      return `${baseClasses} bg-[#57A863] text-white shadow-lg scale-110`;
    }

    if (currentStep > stepIndex + 1) {
      return `${baseClasses} bg-[#9FD6A7] text-white`;
    }

    if (loading) {
      return `${baseClasses} bg-gray-200 text-gray-600 cursor-not-allowed opacity-70`;
    }

    return `${baseClasses} bg-gray-200 text-gray-600 hover:bg-gray-300 hover:scale-105`;
  };

  const getLabelClasses = (stepIndex) => {
    const baseClasses = "mt-2 text-xs font-medium text-center max-w-20";
    return currentStep === stepIndex + 1
      ? `${baseClasses} text-[#57A863]`
      : `${baseClasses} text-gray-600`;
  };

  const getConnectionLineClasses = (stepIndex) => {
    const baseClasses = "h-0.5 w-16 mx-2 transition-colors duration-200";
    return currentStep > stepIndex + 1
      ? `${baseClasses} bg-[#9FD6A7]`
      : `${baseClasses} bg-gray-200`;
  };

  const handleStepClick = (stepIndex) => {
    if (!loading) {
      setCurrentStep(stepIndex + 1);
    }
  };

  const handleKeyDown = (event, stepIndex) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleStepClick(stepIndex);
    }
  };

  const renderStepContent = (stepIndex) => {
    if (currentStep > stepIndex + 1) {
      return (
        <svg
          className="h-6 w-6"
          fill="currentColor"
          viewBox="0 0 20 20"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
            clipRule="evenodd"
          />
        </svg>
      );
    }
    return stepIndex + 1;
  };

  return (
    <div className="mb-6">
      <nav
        className="flex items-center justify-center"
        role="navigation"
        aria-label="Étapes de création d'article"
      >
        {Array.from({ length: totalSteps }, (_, i) => (
          <React.Fragment key={i}>
            <div className="flex flex-col items-center">
              <button
                type="button"
                className={getStepClasses(i)}
                onClick={() => handleStepClick(i)}
                onKeyDown={(event) => handleKeyDown(event, i)}
                disabled={loading}
                aria-label={`Étape ${i + 1}: ${getStepLabel(i)}`}
                aria-current={currentStep === i + 1 ? "step" : undefined}
              >
                {renderStepContent(i)}
              </button>
              <div className={getLabelClasses(i)}>{getStepLabel(i)}</div>
            </div>

            {/* Ligne de connexion */}
            {i < totalSteps - 1 && (
              <div className={getConnectionLineClasses(i)} aria-hidden="true" />
            )}
          </React.Fragment>
        ))}
      </nav>
    </div>
  );
};

StepIndicator.propTypes = {
  currentStep: PropTypes.number.isRequired,
  totalSteps: PropTypes.number.isRequired,
  loading: PropTypes.bool.isRequired,
  setCurrentStep: PropTypes.func.isRequired,
};

export default StepIndicator;